import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React, { useState } from "react"
import { Button, CustomInput, Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import { v4 } from 'uuid'
import { GameIcon } from 'web/components/generic/GameIcon'
import { LabeledCheckboxInput } from "web/components/generic/LabeledCheckbox"
import { LabeledTextInput } from "web/components/generic/LabeledTextInput"
import { WithTooltip } from 'web/components/generic/WithTooltip'
import { WowheadLink } from 'web/components/generic/WowheadLink'
import { getCurrentBuild } from 'web/data/core-data'
import { GuiData } from 'web/data/gui-data'
import { RosterPlayer, Spell, SpellOverride } from "web/data/models"
import { useFocusOnMount } from 'web/hooks/useFocusOnMount'
import { useInputValue } from "web/hooks/useInputValue"
import { useKeyShortcut } from 'web/hooks/useKeyShortcut'
import { useValidatedState } from 'web/hooks/useValidatedState'
import { createGroupedOptions } from "web/utils/jsx-utils"
import { sortSpells } from 'web/utils/sort-utils'
import { joinLines } from "web/utils/utils"
import { ModalContainerProps } from "./_ModalContainer"

export type EditPlayerModalProps = {
    title: string
    saveBtnText: string
    player: RosterPlayer
    rosterPlayerNames: string[]
    onConfirm: (player: RosterPlayer) => void

    /**
     * Defines in what mode the modal was opened.
     */
    mode: "roster-add" | "roster-edit" | "setup-add-custom" | "setup-add-roster" | "setup-edit"
}

// TODO: This thing is like 5 components, break apart please

export const EditPlayerModal: React.FC<EditPlayerModalProps & ModalContainerProps> = (props) => {

    const specOptions = createGroupedOptions({ optionGroups: GuiData.PlayerSpecOptions })
    const buildData = getCurrentBuild()

    const validatedName = useValidatedState({
        value: props.player.name,
        clearErrorOnChange: true,
        onBlur: (value) => {
            return value.trim()
        },
        validator: (value) => {
            if (value?.trim() === '') {
                return 'You must enter a name'
            }
        }
    })

    const playerNameInputRef = useFocusOnMount()

    const [specId, setSpecId] = useInputValue(props.player.specId)

    const [selectedTalents, setSelectedTalents] = useState<string[]>(props.player.talentIds)
    const [selectedModifiers, setSelectedModifiers] = useState<string[]>(props.player.modifierIds)

    const initializeSpellOverrides = (specId: string) => {
        return buildData
            .Spec(specId).spellIds
            .map(buildData.Spell)
            .map(spell => {

                const spellOverride = props.player.overrides
                    .find(so => so.spellId === spell.id) ?? {
                    id: v4(),
                    cooldown: spell.cooldown,
                    ignoreModifiers: false,
                    spellId: spell.id
                }

                return {
                    spell,
                    spellOverride
                }

            })
    }

    const [spellOverrides, setSpellOverrides] = useState(() => initializeSpellOverrides(specId))

    const handleSave = () => {
        if (validatedName.validate() === false) {
            return
        }

        const overrides = spellOverrides
            .filter(override => isSpellModified(override.spell, override.spellOverride))
            .map(override => override.spellOverride)

        props.closeSelf()
        props.onConfirm({
            ...props.player,

            id: props.player.id,
            name: validatedName.value,
            specId,
            talentIds: selectedTalents,
            modifierIds: selectedModifiers,
            overrides
        })
    }

    let talents: JSX.Element[] = []
    let modifiers: JSX.Element[] = []
    let spellOverrideElements: JSX.Element[] = []

    talents = buildData.Spec(specId).talentIds
        .map(talentId => buildData.Talent(talentId))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(talent => {
            return (
                <LabeledCheckboxInput
                    key={talent.id}
                    small
                    label={(
                        <div className={'d-flex align-items-center space-mx-1'}>
                            <WowheadLink gameData={{ ...talent }} disableLeftClick>
                                <GameIcon
                                    className={'border border-dark'}
                                    size="16px"
                                    icon_name={talent.icon_name}
                                />
                            </WowheadLink>
                            <div>
                                {talent.name}
                            </div>
                        </div>
                    )}
                    checked={selectedTalents.includes(talent.id)}
                    onChange={(checked) => {
                        if (checked) {
                            setSelectedTalents([
                                ...selectedTalents,
                                talent.id
                            ])
                        } else {
                            setSelectedTalents(prev => ([
                                ...prev.filter(id => id !== talent.id)
                            ]))
                        }
                    }}
                />
            )
        })

    for (const key in buildData.modifiersById) {
        const value = buildData.Modifier(key)

        if (value.eligibleSpecIds.includes(specId)) {
            modifiers.push((
                <LabeledCheckboxInput
                    key={key}
                    small
                    label={(
                        <div className={'d-flex align-items-center space-mx-1'}>
                            <WowheadLink gameData={{ ...value }} disableLeftClick>
                                <GameIcon
                                    className={'border border-dark'}
                                    size="16px"
                                    icon_name={value.icon_name}
                                />
                            </WowheadLink>
                            <div>
                                {value.name}
                            </div>
                        </div>
                    )}
                    checked={selectedModifiers.includes(value.id)}
                    onChange={(checked) => {
                        if (checked) {
                            setSelectedModifiers(prev => [
                                ...prev,
                                value.id
                            ])
                        } else {
                            setSelectedModifiers(prev => [
                                ...prev.filter(id => id !== value.id)
                            ])
                        }
                    }}
                />
            ))
        }
    }

    spellOverrideElements = Object.values(spellOverrides)
        .sort((a, b) => sortSpells(a.spell, b.spell))
        .map(item => {
            const { spell, spellOverride } = item

            return (
                <SpellOverrideRow
                    key={spell.id}
                    spell={spell}
                    spellOverride={spellOverride}

                    onChange={(p) => {
                        setSpellOverrides(([
                            ...spellOverrides.filter(i => i.spell.id !== spell.id),
                            {
                                spell,
                                spellOverride: {
                                    id: v4(),
                                    spellId: spell.id,
                                    cooldown: p.newCooldown,
                                    ignoreModifiers: p.ignoreModifiers,
                                }
                            }
                        ]))
                    }}
                />
            )
        })

    useKeyShortcut({
        key: "s",
        modifier: "ctrl",
        callback: handleSave,
    })

    return (
        <Modal
            centered
            isOpen={true}
            fade={false}
            size="lg"
            backdrop={"static"}
            toggle={props.closeSelf}
        >
            <ModalHeader className="d-flex justify-content-between">
                {props.title}
            </ModalHeader>

            <ModalBody className="p-3 m-0 bg-light">

                <div className="d-flex flex-column space-my-3">
                    <div className="d-flex space-mx-3">
                        {/* Spec & Name START */}
                        <div className="d-flex flex-column space-my-3 w-50">
                            <LabeledTextInput
                                innerRef={playerNameInputRef}
                                boldLabel
                                label="Name"
                                small
                                value={validatedName.value}
                                invalid={validatedName.validationError != null}
                                onChange={(e) => validatedName.setValue(e.target.value)}
                                onBlur={validatedName.onBlur}

                                suggestbox={{
                                    options: props.rosterPlayerNames
                                }}
                            />

                            <div className="d-flex flex-column flex-grow-1">
                                <label className="font-weight-bold small">{"Spec"}</label>
                                <div className="d-flex">
                                    <div className="mr-2 border border-dark">
                                        <GameIcon
                                            size="29px"
                                            icon_name={buildData.Spec(specId).icon_name}
                                        />
                                    </div>
                                    <CustomInput
                                        id="player-spec-select"
                                        bsSize="sm"
                                        type="select"
                                        value={specId}
                                        onChange={(e) => {
                                            setSpecId(e)

                                            setSelectedTalents([])
                                            setSelectedModifiers([])
                                            setSpellOverrides(initializeSpellOverrides(e.target.value))
                                        }}
                                    >
                                        {specOptions}
                                    </CustomInput>
                                </div>
                            </div>
                        </div>
                        {/* Spec & Name END */}

                        {/* Talents & Mods START */}
                        {(talents.length <= 0 && modifiers.length <= 0) &&
                            <div className="d-flex flex-column w-100">
                                <label className="small font-weight-bold">
                                    {"Talents & Modifiers"}
                                </label>
                                <div className="d-flex bg-white p-3 border w-100 h-100 small text-muted font-italic align-items-center justify-content-center">
                                    {"This spec has no meaningful talents or modifiers"}
                                </div>
                            </div>
                        }

                        {(talents.length >= 1 || modifiers.length >= 1) &&
                            <div className="d-flex flex-column w-100">
                                <label className="small font-weight-bold">
                                    {"Talents & Modifiers"}
                                </label>
                                <div className="d-flex bg-white p-3 border w-100 h-100">
                                    {talents.length >= 1 &&
                                        <div className="d-flex flex-column w-50">

                                            <div className="d-flex justify-content-between align-items-center">
                                                <label
                                                    style={{ marginTop: "-4px" }}
                                                    className="small">
                                                    {"Talents"}
                                                </label>
                                            </div>

                                            <div className="d-flex flex-column space-my-1">
                                                {talents}
                                            </div>
                                        </div>
                                    }

                                    {modifiers.length >= 1 &&
                                        <div className="d-flex flex-column w-50">
                                            <label
                                                style={{ marginTop: "-4px" }}
                                                className="small">
                                                {"Modifiers"}
                                            </label>
                                            <div className="d-flex flex-column space-my-2">
                                                {modifiers}
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                        {/* Talents & Mods END */}
                    </div>

                    {/* Overrides START */}
                    <div className="d-flex flex-column">
                        <label className="font-weight-bold small">{"Cooldown overrides"}</label>

                        <div className="d-flex flex-column space-my-2 p-2 border bg-white">

                            {/* Headers START */}
                            <div className="row no-gutters small">
                                <div className="col-6 pr-2">
                                    <label className="mb-0">
                                        {"Spell name"}
                                    </label>
                                </div>

                                <div className="col-2 pr-2">
                                    <label className="mb-0">
                                        {"Base cooldown"}
                                    </label>
                                </div>

                                <div className="col-3 pr-2">
                                    <div className="d-flex space-mx-2 align-items-center">
                                        <label className="mb-0">
                                            {"Ignore modifiers"}
                                        </label>
                                        <WithTooltip place="top">
                                            {ttId => (
                                                <FontAwesomeIcon
                                                    data-for={ttId}
                                                    data-tip={joinLines([
                                                        `Selected talents/modifiers might modify the`,
                                                        `cooldown further. If you want full control of`,
                                                        `the cooldown duration, select "yes" here.`
                                                    ])}
                                                    fixedWidth
                                                    icon={faQuestionCircle}
                                                />
                                            )}
                                        </WithTooltip>
                                    </div>
                                </div>
                            </div>
                            {/* Headers END */}

                            {spellOverrideElements}

                        </div>
                    </div>
                    {/* Overrides END */}

                </div>

                {/* TODO: Can't implement this because we can't listen to shift-key while a select list is focused. */}
                {/* {props.mode === 'setup-add-roster' &&
                    <div className="bg-white small p-2 text-muted text-center mt-3">
                        {"Tip: you can shift+click a player in the list to skip this modal in the future."}
                    </div>
                } */}

            </ModalBody>

            <ModalFooter className="d-flex justify-content-end space-mx-2" >
                <Button
                    color="success"
                    size="sm"
                    onClick={handleSave}
                    title={`${props.saveBtnText} (ctrl+s)`}
                >
                    {props.saveBtnText}
                </Button>

                <Button
                    outline
                    size="sm"
                    onClick={props.closeSelf}
                    title={"Cancel edit (esc)"}
                >
                    {"Cancel edit"}
                </Button>
            </ModalFooter>
        </Modal >
    )
}

type SpellOverrideRowProps = {
    spell: Spell
    spellOverride: SpellOverride
    onChange: (payload: {
        newCooldown: number
        ignoreModifiers: boolean
    }) => void
}

const SpellOverrideRow: React.FC<SpellOverrideRowProps> = (props) => {

    const { spell, spellOverride, onChange } = props
    const isModified = isSpellModified(spell, spellOverride)
    const [cooldown, setCooldown] = useState<number>(spellOverride.cooldown)

    const [ignoreModifiers, setIgnoreModifiers] = useState<boolean>(
        spellOverride.ignoreModifiers
    )

    const handleCooldownOnBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        const { value } = e.target

        let actualValue = (value.trim() === '')
            ? spellOverride.cooldown
            : parseInt(value)

        // Somewhere between 0 and 30min? Maybe someone will use it for Vanilla.
        if (actualValue <= 0 || actualValue > 1800) {
            actualValue = spellOverride.cooldown
        }

        setCooldown(actualValue)

        onChange({
            ignoreModifiers,
            newCooldown: actualValue,
        })
    }

    const handleIgnoreModifiersOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = e.target
        setIgnoreModifiers(value === 'true')

        onChange({
            ignoreModifiers: value === 'true',
            newCooldown: cooldown
        })
    }

    const handleResetClick = () => {
        setCooldown(spell.cooldown)
        setIgnoreModifiers(false)

        onChange({
            ignoreModifiers: false,
            newCooldown: spell.cooldown
        })
    }

    return (
        <div className="row no-gutters">

            <div className="col-6 pr-2">
                <div className="d-flex">
                    <WowheadLink
                        gameData={{ ...spell }}
                        className='mr-2 border border-dark'
                    >
                        <GameIcon
                            size="29px"
                            icon_name={spell.icon_name}
                        />
                    </WowheadLink>
                    <Input
                        bsSize="sm"
                        defaultValue={spell.name}
                        disabled
                        readOnly
                    />
                </div>
            </div>

            <div className="col-2 pr-2">
                <Input
                    className={classNames(
                        { "border-info": spellOverride.cooldown !== spell.cooldown }
                    )}
                    type="number"
                    bsSize="sm"
                    value={cooldown}
                    onChange={e => setCooldown(parseInt(e.target.value))}
                    onBlur={handleCooldownOnBlur}
                />
            </div>

            <div className="col-3 pr-2">
                <CustomInput
                    id="ignore-modifiers-select"
                    className={classNames(
                        { "border-info": ignoreModifiers }
                    )}
                    type="select"
                    bsSize="sm"
                    value={`${ignoreModifiers}`}
                    onChange={handleIgnoreModifiersOnChange}
                >
                    <option value={"true"}>{"Yes"}</option>
                    <option value={"false"}>{"No"}</option>
                </CustomInput>
            </div>

            <div className="col-1">
                <Button
                    block
                    size="sm"
                    outline
                    disabled={isModified === false}
                    onClick={handleResetClick}
                >
                    {"Reset"}
                </Button>
            </div>

        </div>
    )
}

function isSpellModified(spell: Spell, spellOverride: SpellOverride) {
    if (spellOverride.ignoreModifiers !== false) {
        return true
    }

    if (spellOverride.cooldown !== spell.cooldown) {
        return true
    }

    return false
}
